/**
 * R11/D hotfix — Schema-tolerant readers for voce.parametri_estratti and
 * voce.parametri_inferiti.
 *
 * After the R11/D migration, parametri PXX values can be either:
 *   • LEGACY scalar:        "AISI 304"  /  "DN20"  /  20.0
 *   • R11 dict-shape:       { value: "AISI 304", source: "legacy",
 *                             motivazione: null, learned_rule_id: null }
 *
 * Without unwrapping, React rendering crashes with:
 *   "Objects are not valid as a React child (found: object with keys
 *    {value, source, motivazione, learned_rule_id})"
 *
 * These helpers normalize to the underlying scalar value transparently so
 * UI components can ignore the schema difference. Mirrors the backend
 * helpers in services/voci_schema.py (get_value, get_value_from).
 */

/**
 * Unwrap a single field. If the value is a R11 dict-shape, return its
 * `value` property; otherwise return as-is. Null/undefined pass through.
 */
export function unwrapParam(value) {
  if (value === null || value === undefined) return value;
  if (typeof value === "object" && !Array.isArray(value) && "value" in value) {
    return value.value;
  }
  return value;
}

/**
 * Read a parametric field from a bucket (parametri_estratti or
 * parametri_inferiti), automatically unwrapping the R11 dict-shape.
 *
 * Returns `defaultValue` (default: undefined) when the bucket is empty
 * or the key is missing.
 */
export function getValueFrom(bucket, key, defaultValue) {
  if (!bucket) return defaultValue;
  if (!(key in bucket)) return defaultValue;
  return unwrapParam(bucket[key]);
}

/**
 * Read a parametric field from a voce — first from parametri_estratti,
 * then from parametri_inferiti. Returns `defaultValue` if not found.
 */
export function getValue(voce, key, defaultValue) {
  if (!voce) return defaultValue;
  const estr = voce.parametri_estratti;
  if (estr && key in estr) return unwrapParam(estr[key]);
  const inf = voce.parametri_inferiti;
  if (inf && key in inf) return unwrapParam(inf[key]);
  return defaultValue;
}

/**
 * Read the source meta of an R11 dict-shape field. Returns null for
 * legacy scalar values (which have no source meta).
 */
export function getSourceFrom(bucket, key) {
  if (!bucket) return null;
  const val = bucket[key];
  if (val && typeof val === "object" && !Array.isArray(val) && "source" in val) {
    return val.source;
  }
  return null;
}
